import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import reportAPI, { GstBooksFilter } from "../../services/reportAPI";
import { rowsToCsv } from "../../utils/csvExport";
import financialYearAPI, {
  formatFinancialYearDisplay,
  indiaFyMeta,
} from "../../services/financialYearAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useAuthStore } from "../../stores/authStore";

type BookTab =
  | "gstr1"
  | "gstr3b"
  | "purchase"
  | "itc"
  | "daybook"
  | "party"
  | "pnl"
  | "tally"
  | "expiry"
  | "stock"
  | "balance"
  | "trial"
  | "cash"
  | "refund"
  | "nfc"
  | "slow"
  | "operator";

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const defaultStart = () => indiaFyMeta().startDate;
const defaultEnd = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  const fyEnd = indiaFyMeta().endDate;
  return today < fyEnd ? today : fyEnd;
};

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(filename: string, rows: Array<Record<string, unknown>>) {
  if (!rows.length) {
    downloadJson(filename.replace(/\.csv$/, ".json"), { rows: [] });
    return;
  }
  const blob = new Blob([rowsToCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const GstBooksPage: React.FC = () => {
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";

  const [tab, setTab] = useState<BookTab>("gstr1");
  const [draft, setDraft] = useState<GstBooksFilter>({
    startDate: defaultStart(),
    endDate: defaultEnd(),
    branchId: isAdmin ? user?.branchId || undefined : undefined,
    salesPointId: undefined,
    fyCode: indiaFyMeta().code,
  });
  const [filters, setFilters] = useState<GstBooksFilter>(draft);

  const { data: financialYears = [] } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const branchSalesPoints = useMemo(() => {
    const bid = isAdmin ? user?.branchId : draft.branchId;
    if (!bid) return salesPoints;
    return salesPoints.filter((d) => d.branchId === bid);
  }, [salesPoints, draft.branchId, isAdmin, user?.branchId]);

  useEffect(() => {
    if (isAdmin && user?.branchId) {
      setDraft((p) => ({ ...p, branchId: user.branchId || undefined }));
    }
  }, [isAdmin, user?.branchId]);

  const enabled = Boolean(filters.startDate && filters.endDate);

  const gstr1Q = useQuery({
    queryKey: ["gst-gstr1", filters],
    queryFn: () => reportAPI.getGstr1(filters),
    enabled: enabled && tab === "gstr1",
  });
  const gstr3bQ = useQuery({
    queryKey: ["gst-gstr3b", filters],
    queryFn: () => reportAPI.getGstr3b(filters),
    enabled: enabled && tab === "gstr3b",
  });
  const purchaseQ = useQuery({
    queryKey: ["gst-purchase", filters],
    queryFn: () => reportAPI.getPurchaseRegister(filters),
    enabled: enabled && tab === "purchase",
  });
  const itcQ = useQuery({
    queryKey: ["gst-itc", filters],
    queryFn: () => reportAPI.getItcRegister(filters),
    enabled: enabled && tab === "itc",
  });
  const dayBookQ = useQuery({
    queryKey: ["gst-daybook", filters],
    queryFn: () => reportAPI.getDayBook(filters),
    enabled: enabled && tab === "daybook",
  });
  const partyQ = useQuery({
    queryKey: ["gst-party", filters],
    queryFn: () => reportAPI.getPartyLedger(filters),
    enabled: enabled && tab === "party",
  });
  const pnlQ = useQuery({
    queryKey: ["gst-pnl", filters],
    queryFn: () => reportAPI.getProfitLoss(filters),
    enabled: enabled && tab === "pnl",
  });
  const tallyQ = useQuery({
    queryKey: ["gst-tally", filters],
    queryFn: () => reportAPI.getTallyExport(filters),
    enabled: enabled && tab === "tally",
  });
  const expiryQ = useQuery({
    queryKey: ["gst-expiry", filters],
    queryFn: () => reportAPI.getExpiryReport(filters),
    enabled: enabled && tab === "expiry",
  });
  const stockQ = useQuery({
    queryKey: ["gst-stock", filters],
    queryFn: () => reportAPI.getStockLedger(filters),
    enabled: enabled && tab === "stock",
  });
  const balanceQ = useQuery({
    queryKey: ["gst-balance", filters],
    queryFn: () => reportAPI.getBalanceSheet(filters),
    enabled: enabled && tab === "balance",
  });
  const trialQ = useQuery({
    queryKey: ["gst-trial", filters],
    queryFn: () => reportAPI.getTrialBalance(filters),
    enabled: enabled && tab === "trial",
  });
  const cashQ = useQuery({
    queryKey: ["gst-cash", filters],
    queryFn: () => reportAPI.getCashBook(filters),
    enabled: enabled && tab === "cash",
  });
  const refundQ = useQuery({
    queryKey: ["gst-refund", filters],
    queryFn: () => reportAPI.getRefundRegister(filters),
    enabled: enabled && tab === "refund",
  });
  const nfcQ = useQuery({
    queryKey: ["gst-nfc", filters],
    queryFn: () => reportAPI.getNfcLiability(filters),
    enabled: enabled && tab === "nfc",
  });
  const slowQ = useQuery({
    queryKey: ["gst-slow", filters],
    queryFn: () => reportAPI.getSlowDeadStock(filters),
    enabled: tab === "slow",
  });
  const operatorQ = useQuery({
    queryKey: ["gst-operator", filters],
    queryFn: () => reportAPI.getOperatorSales(filters),
    enabled: enabled && tab === "operator",
  });

  const tabQueries: Record<BookTab, typeof gstr1Q> = {
    gstr1: gstr1Q,
    gstr3b: gstr3bQ,
    purchase: purchaseQ,
    itc: itcQ,
    daybook: dayBookQ,
    party: partyQ,
    pnl: pnlQ,
    tally: tallyQ,
    expiry: expiryQ,
    stock: stockQ,
    balance: balanceQ,
    trial: trialQ,
    cash: cashQ,
    refund: refundQ,
    nfc: nfcQ,
    slow: slowQ,
    operator: operatorQ,
  };
  const active = tabQueries[tab];

  const errorMessage = active.error
    ? active.error instanceof Error
      ? active.error.message
      : "Failed to load report"
    : "";

  const periodLabel = `${filters.startDate}_to_${filters.endDate}`;

  const handleDownloadJson = () => {
    if (!active.data) return;
    downloadJson(`${tab}_${periodLabel}.json`, active.data);
  };

  const handleDownloadCsv = () => {
    if (!active.data) return;
    if (tab === "gstr1") {
      downloadCsv(`${tab}_hsn_${periodLabel}.csv`, active.data.hsn || []);
      if (Array.isArray(active.data.b2cs) && active.data.b2cs.length) {
        downloadCsv(`${tab}_b2cs_${periodLabel}.csv`, active.data.b2cs);
      }
      if (Array.isArray(active.data.cdnr?.notes) && active.data.cdnr.notes.length) {
        downloadCsv(`${tab}_cdnr_${periodLabel}.csv`, active.data.cdnr.notes);
      }
      return;
    }
    if (tab === "gstr3b") {
      downloadCsv(`${tab}_${periodLabel}.csv`, [
        { section: "3.1", ...active.data.table_3_1 },
        { section: "4_itc", ...active.data.table_4_itc },
        { section: "net", ...active.data.netTaxLiability },
      ]);
      return;
    }
    if (tab === "purchase") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.lines || []);
      return;
    }
    if (tab === "itc") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.entries || []);
      return;
    }
    if (tab === "daybook") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.entries || []);
      return;
    }
    if (tab === "party") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.parties || []);
      return;
    }
    if (tab === "pnl") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.byCategory || []);
      return;
    }
    if (tab === "tally") {
      downloadCsv(`tally_${periodLabel}.csv`, active.data.rows || []);
      return;
    }
    if (tab === "expiry") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.items || []);
      return;
    }
    if (tab === "stock") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.movements || []);
      return;
    }
    if (tab === "trial") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.rows || []);
      return;
    }
    if (tab === "cash") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.entries || []);
      return;
    }
    if (tab === "refund") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.creditNotes || []);
      return;
    }
    if (tab === "nfc") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.holders || []);
      return;
    }
    if (tab === "slow") {
      downloadCsv(`slow_dead_stock.csv`, active.data.items || []);
      return;
    }
    if (tab === "operator") {
      downloadCsv(`${tab}_${periodLabel}.csv`, active.data.operators || []);
      return;
    }
    if (tab === "balance") {
      downloadCsv(`${tab}_${periodLabel}.csv`, [
        { section: "assets", ...active.data.assets },
        { section: "liabilities", ...active.data.liabilities },
        { section: "equity", ...active.data.equity },
      ]);
      return;
    }
  };

  const handleDownloadPortalExport = async () => {
    try {
      const data = await reportAPI.getGstr1PortalExport(filters);
      downloadJson(`gstr1_portal_${periodLabel}.json`, data);
      const sheets = data?.sheets || {};
      if (sheets.b2cs?.length) {
        downloadCsv(`gstr1_portal_b2cs_${periodLabel}.csv`, sheets.b2cs);
      }
      if (sheets.hsn?.length) {
        downloadCsv(`gstr1_portal_hsn_${periodLabel}.csv`, sheets.hsn);
      }
      if (sheets.cdnr?.length) {
        downloadCsv(`gstr1_portal_cdnr_${periodLabel}.csv`, sheets.cdnr);
      }
      if (sheets.invoices?.length) {
        downloadCsv(`gstr1_portal_invoices_${periodLabel}.csv`, sheets.invoices);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box>
      <PageHeader
        title="GST & books"
        subtitle="GSTR-1/3B, ITC register, purchase register, day-book, P&L, Tally export"
        action={
          <Stack direction="row" spacing={1}>
            {tab === "gstr1" && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon />}
                disabled={!enabled}
                onClick={handleDownloadPortalExport}
              >
                Portal / CA
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!active.data}
              onClick={handleDownloadJson}
            >
              JSON
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={!active.data}
              onClick={handleDownloadCsv}
            >
              CSV
            </Button>
          </Stack>
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial year</InputLabel>
              <Select
                label="Financial year"
                value={draft.fyCode || ""}
                onChange={(e) => {
                  const code = e.target.value as string;
                  const fy = financialYears.find((f) => f.code === code);
                  setDraft((p) => ({
                    ...p,
                    fyCode: code || undefined,
                    startDate: fy?.startDate || p.startDate,
                    endDate: fy?.endDate || p.endDate,
                  }));
                }}
              >
                {financialYears.map((fy) => (
                  <MenuItem key={fy.id} value={fy.code}>
                    {formatFinancialYearDisplay(fy)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={draft.startDate}
              onChange={(e) =>
                setDraft((p) => ({ ...p, startDate: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={draft.endDate}
              onChange={(e) =>
                setDraft((p) => ({ ...p, endDate: e.target.value }))
              }
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={draft.branchId || ""}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      branchId: e.target.value || undefined,
                      salesPointId: undefined,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sales point</InputLabel>
              <Select
                label="Sales point"
                value={draft.salesPointId || ""}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    salesPointId: e.target.value || undefined,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                {branchSalesPoints.map((sp) => (
                  <MenuItem key={sp.id} value={sp.id}>
                    {sp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setFilters({ ...draft })}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        <Tab value="gstr1" label="GSTR-1 / HSN" />
        <Tab value="gstr3b" label="GSTR-3B" />
        <Tab value="purchase" label="Purchase register" />
        <Tab value="itc" label="ITC register" />
        <Tab value="daybook" label="Day book" />
        <Tab value="party" label="Party ledger" />
        <Tab value="pnl" label="P&L" />
        <Tab value="tally" label="Tally export" />
        <Tab value="expiry" label="Expiry" />
        <Tab value="stock" label="Stock ledger" />
        <Tab value="balance" label="Balance sheet" />
        <Tab value="trial" label="Trial balance" />
        <Tab value="cash" label="Cash book" />
        <Tab value="refund" label="Refunds" />
        <Tab value="nfc" label="NFC liability" />
        <Tab value="slow" label="Slow stock" />
        <Tab value="operator" label="Operator sales" />
      </Tabs>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {active.isLoading && (
        <Typography color="text.secondary">Loading…</Typography>
      )}

      {tab === "gstr1" && gstr1Q.data && (
        <Stack spacing={2}>
          {gstr1Q.data.companyProfile?.gstin ||
          gstr1Q.data.companyProfile?.legalName ? (
            <Typography variant="body2" color="text.secondary">
              {gstr1Q.data.companyProfile.legalName || "Company"}
              {gstr1Q.data.companyProfile.gstin
                ? ` · GSTIN ${gstr1Q.data.companyProfile.gstin}`
                : ""}
              {gstr1Q.data.companyProfile.stateCode
                ? ` · State ${gstr1Q.data.companyProfile.stateCode}`
                : ""}
            </Typography>
          ) : null}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">Invoices</Typography>
                <Typography fontWeight={600}>
                  {gstr1Q.data.summary.invoiceCount}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">Taxable</Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(gstr1Q.data.summary.taxableValue)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">CGST</Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(gstr1Q.data.summary.cgstAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">SGST</Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(gstr1Q.data.summary.sgstAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">IGST</Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(gstr1Q.data.summary.igstAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption">Invoice value</Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(gstr1Q.data.summary.invoiceValue)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>HSN</TableCell>
                  <TableCell align="right">Rate %</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Taxable</TableCell>
                  <TableCell align="right">CGST</TableCell>
                  <TableCell align="right">SGST</TableCell>
                  <TableCell align="right">IGST</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(gstr1Q.data.hsn || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.hsnCode)}</TableCell>
                      <TableCell align="right">{Number(row.rate)}</TableCell>
                      <TableCell align="right">{Number(row.quantity)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxableAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.cgstAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.sgstAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.igstAmount))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "purchase" && purchaseQ.data && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="body2">
              {purchaseQ.data.summary.purchaseCount} purchases ·{" "}
              {purchaseQ.data.summary.lineCount} lines · Taxable{" "}
              {formatCurrency(purchaseQ.data.summary.taxableValue)} · Tax{" "}
              {formatCurrency(purchaseQ.data.summary.taxAmount)}
            </Typography>
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Purchase #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>HSN</TableCell>
                  <TableCell align="right">Taxable</TableCell>
                  <TableCell align="right">Tax</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(purchaseQ.data.lines || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.purchaseNumber)}</TableCell>
                      <TableCell>{String(row.supplierName || "—")}</TableCell>
                      <TableCell>{String(row.productName || "—")}</TableCell>
                      <TableCell>{String(row.hsnCode || "—")}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxableAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxAmount))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "itc" && itcQ.data && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="body2">
              {itcQ.data.summary.purchaseLines} purchase lines ·{" "}
              {itcQ.data.summary.debitNoteCount} debit notes · Net taxable{" "}
              {formatCurrency(itcQ.data.summary.taxableValue)} · ITC tax{" "}
              {formatCurrency(itcQ.data.summary.taxAmount)} (CGST{" "}
              {formatCurrency(itcQ.data.summary.cgstAmount)} · SGST{" "}
              {formatCurrency(itcQ.data.summary.sgstAmount)} · IGST{" "}
              {formatCurrency(itcQ.data.summary.igstAmount)})
            </Typography>
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Document #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Party</TableCell>
                  <TableCell>GSTIN</TableCell>
                  <TableCell>Linked</TableCell>
                  <TableCell>HSN / Product</TableCell>
                  <TableCell align="right">Taxable</TableCell>
                  <TableCell align="right">Tax</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(itcQ.data.entries || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.entryType)}</TableCell>
                      <TableCell>{String(row.documentNumber)}</TableCell>
                      <TableCell>
                        {row.documentDate
                          ? format(new Date(String(row.documentDate)), "dd MMM yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell>{String(row.partyName || "—")}</TableCell>
                      <TableCell>{String(row.partyGstin || "—")}</TableCell>
                      <TableCell>{String(row.linkedDocument || "—")}</TableCell>
                      <TableCell>
                        {String(row.hsnCode || row.productName || "—")}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxableAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxAmount))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "daybook" && dayBookQ.data && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="body2">
              {dayBookQ.data.summary.entryCount} entries · Debit{" "}
              {formatCurrency(dayBookQ.data.summary.totalDebit)} · Credit{" "}
              {formatCurrency(dayBookQ.data.summary.totalCredit)}
            </Typography>
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Voucher</TableCell>
                  <TableCell>Narration</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dayBookQ.data.entries || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.date)}</TableCell>
                      <TableCell>{String(row.voucherType)}</TableCell>
                      <TableCell>{String(row.voucherNo)}</TableCell>
                      <TableCell>{String(row.narration)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.debit))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.credit))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "party" && partyQ.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Party</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Vouchers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(partyQ.data.parties || []).map(
                (row: Record<string, unknown>, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{String(row.partyType)}</TableCell>
                    <TableCell>{String(row.partyName)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(row.debit))}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(row.credit))}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(row.balance))}
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.voucherCount)}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === "gstr3b" && gstr3bQ.data && (
        <Stack spacing={2}>
          {gstr3bQ.data.companyProfile?.gstin ||
          gstr3bQ.data.companyProfile?.legalName ? (
            <Typography variant="body2" color="text.secondary">
              {gstr3bQ.data.companyProfile.legalName || "Company"}
              {gstr3bQ.data.companyProfile.gstin
                ? ` · GSTIN ${gstr3bQ.data.companyProfile.gstin}`
                : ""}
              {gstr3bQ.data.companyProfile.stateCode
                ? ` · State ${gstr3bQ.data.companyProfile.stateCode}`
                : ""}
            </Typography>
          ) : null}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              3.1 Outward (net of credit notes)
            </Typography>
            <Typography variant="body2">
              Taxable {formatCurrency(gstr3bQ.data.table_3_1.taxableValue)} ·
              CGST {formatCurrency(gstr3bQ.data.table_3_1.cgstAmount)} · SGST{" "}
              {formatCurrency(gstr3bQ.data.table_3_1.sgstAmount)} · IGST{" "}
              {formatCurrency(gstr3bQ.data.table_3_1.igstAmount)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              4 ITC (purchase register)
            </Typography>
            <Typography variant="body2">
              Tax {formatCurrency(gstr3bQ.data.table_4_itc.taxAmount)} · CGST{" "}
              {formatCurrency(gstr3bQ.data.table_4_itc.cgstAmount)} · SGST{" "}
              {formatCurrency(gstr3bQ.data.table_4_itc.sgstAmount)} · IGST{" "}
              {formatCurrency(gstr3bQ.data.table_4_itc.igstAmount)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Net tax liability
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(gstr3bQ.data.netTaxLiability.taxAmount)} (CGST{" "}
              {formatCurrency(gstr3bQ.data.netTaxLiability.cgstAmount)} · SGST{" "}
              {formatCurrency(gstr3bQ.data.netTaxLiability.sgstAmount)} · IGST{" "}
              {formatCurrency(gstr3bQ.data.netTaxLiability.igstAmount)})
            </Typography>
          </Paper>
        </Stack>
      )}

      {tab === "pnl" && pnlQ.data && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="body2">
              Net sales {formatCurrency(pnlQ.data.summary.netSales)} · COGS{" "}
              {formatCurrency(pnlQ.data.summary.cogs)} · Gross profit{" "}
              {formatCurrency(pnlQ.data.summary.grossProfit)} (
              {pnlQ.data.summary.grossMarginPct}%)
            </Typography>
          </Paper>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">COGS</TableCell>
                  <TableCell align="right">Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(pnlQ.data.byCategory || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={String(row.categoryId || i)}>
                      <TableCell>
                        {String(
                          row.categoryName ||
                            (row.categoryId === "uncategorized"
                              ? "Uncategorized"
                              : row.categoryId) ||
                            "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.revenue))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.cogs))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.margin))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "tally" && tallyQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            {tallyQ.data.rows?.length || 0} voucher rows — download CSV for
            Tally/Busy import
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Voucher</TableCell>
                  <TableCell>Ledger</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tallyQ.data.rows || []).slice(0, 100).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.date)}</TableCell>
                      <TableCell>{String(row.voucherType)}</TableCell>
                      <TableCell>{String(row.voucherNo)}</TableCell>
                      <TableCell>{String(row.ledgerName)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.debit))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.credit))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "expiry" && expiryQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            Expired {expiryQ.data.summary.expiredCount} · Near expiry{" "}
            {expiryQ.data.summary.nearExpiryCount}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(expiryQ.data.items || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.productName || "—")}</TableCell>
                      <TableCell>{String(row.batchNo || "—")}</TableCell>
                      <TableCell>{String(row.expiryDate)}</TableCell>
                      <TableCell align="right">
                        {Number(row.availableQuantity)}
                      </TableCell>
                      <TableCell>
                        {row.expired ? "Expired" : `In ${row.daysLeft}d`}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "stock" && stockQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            {stockQ.data.summary.movementCount} movements
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Delta</TableCell>
                  <TableCell align="right">After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(stockQ.data.movements || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>
                        {String(row.createdAt).slice(0, 19)}
                      </TableCell>
                      <TableCell>{String(row.productName || "—")}</TableCell>
                      <TableCell>{String(row.movementType)}</TableCell>
                      <TableCell align="right">
                        {Number(row.quantityDelta)}
                      </TableCell>
                      <TableCell align="right">{Number(row.qtyAfter)}</TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "balance" && balanceQ.data && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2">Assets</Typography>
            <Typography variant="body2">
              Inventory {formatCurrency(balanceQ.data.assets.inventory)} · Cash{" "}
              {formatCurrency(balanceQ.data.assets.cashAndBank)} · Total{" "}
              {formatCurrency(balanceQ.data.assets.total)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2">Liabilities</Typography>
            <Typography variant="body2">
              NFC {formatCurrency(balanceQ.data.liabilities.nfcWalletLiability)} ·
              Supplier {formatCurrency(balanceQ.data.liabilities.supplierPayable)} ·
              GST {formatCurrency(balanceQ.data.liabilities.gstPayable)}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2">Equity</Typography>
            <Typography variant="body2">
              Retained {formatCurrency(balanceQ.data.equity.retainedEarnings)}
            </Typography>
          </Paper>
        </Stack>
      )}

      {tab === "trial" && trialQ.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ledger</TableCell>
                <TableCell>Group</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(trialQ.data.rows || []).map(
                (row: Record<string, unknown>, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{String(row.ledger)}</TableCell>
                    <TableCell>{String(row.group)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(row.debit))}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(row.credit))}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === "cash" && cashQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            Receipts {formatCurrency(cashQ.data.summary.totalReceipts)} ·
            Payments {formatCurrency(cashQ.data.summary.totalPayments)} · Net{" "}
            {formatCurrency(cashQ.data.summary.netCash)}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Ref</TableCell>
                  <TableCell align="right">Receipt</TableCell>
                  <TableCell align="right">Payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(cashQ.data.entries || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.date)}</TableCell>
                      <TableCell>{String(row.type)}</TableCell>
                      <TableCell>{String(row.ref)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.receipt))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.payment))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "refund" && refundQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            {refundQ.data.summary.creditNoteCount} credit notes · Refunded{" "}
            {formatCurrency(refundQ.data.summary.totalRefunded)} · Tax reversed{" "}
            {formatCurrency(refundQ.data.summary.totalTaxReversed)}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CN #</TableCell>
                  <TableCell>Sale</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(refundQ.data.creditNotes || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.creditNoteNumber)}</TableCell>
                      <TableCell>{String(row.saleNumber || "—")}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.netAmount))}
                      </TableCell>
                      <TableCell>{String(row.reason || "—")}</TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "nfc" && nfcQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            Outstanding liability{" "}
            {formatCurrency(nfcQ.data.summary.outstandingLiability)} · Recharged
            in period {formatCurrency(nfcQ.data.summary.rechargedInPeriod)}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Card</TableCell>
                  <TableCell>Holder</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(nfcQ.data.holders || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.cardDisplay)}</TableCell>
                      <TableCell>{String(row.holderName)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.balance))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "slow" && slowQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            {slowQ.data.summary.itemCount} slow items · Value{" "}
            {formatCurrency(slowQ.data.summary.totalStockValue)} (no sale in{" "}
            {slowQ.data.noSaleDays}d)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(slowQ.data.items || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.productName || "—")}</TableCell>
                      <TableCell>{String(row.sku || "—")}</TableCell>
                      <TableCell align="right">
                        {Number(row.availableQuantity)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.stockValue))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === "operator" && operatorQ.data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            {operatorQ.data.summary.operatorCount} operators ·{" "}
            {operatorQ.data.summary.totalSales} sales · Net{" "}
            {formatCurrency(operatorQ.data.summary.totalNet)}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Operator</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Tax</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(operatorQ.data.operators || []).map(
                  (row: Record<string, unknown>, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{String(row.operatorName)}</TableCell>
                      <TableCell>{String(row.role || "—")}</TableCell>
                      <TableCell align="right">{Number(row.saleCount)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.netAmount))}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.taxAmount))}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  );
};

export default GstBooksPage;
